import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useUniversitySession } from '@/hooks/useUniversitySession'
import QuizInterface from '@/components/education/QuizInterface'
import ShortAnswerBox from '@/components/university/university/ShortAnswerBox'
import EssayDropbox from '@/components/university/university/EssayDropbox'
import { ModuleMilestone } from '@/components/university/university/ModuleMilestone'
import SafeVideoPlayer from '@/components/video/SafeVideoPlayer'
import LanguagePicker from '@/components/university/ui/LanguagePicker'
import SEO from '@/components/university/common/SEO'
import { getEntitlements } from '@/utils/entitlements'

// ─── Design Tokens ─────────────────────────────────────────────────────────────
const M = '#06060c'
const G = '#d4a843'
const IN = '#0ea5e9'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.05)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'

// ─── Types ─────────────────────────────────────────────────────────────────────
interface LessonMeta {
  lesson_id: number
  title: string
  learning_objective: string
  core_concepts: string[]
}
interface ModuleMeta {
  module_id: number
  title: string
  lessons: LessonMeta[]
}
type EssayMode = 'none' | 'short_answer_only' | 'essay_per_module' | 'capstone_only'
interface ClassInfo {
  id: string
  name: string
  module_plan: ModuleMeta[]
  essay_mode: EssayMode
}

// ─── Strip EDU_UPDATE artefacts from display text ──────────────────────────────
// The AI appends EDU_UPDATE::{...} at the end of every message.
// Partial fragments ("EDU", "EDU_UPDATE") can leak during streaming.
function cleanEduContent(raw: string): string {
  return raw
    .replace(/EDU_UPDATE::\{[^}]*\}/g, '')
    .replace(/EDU_UPDATE::/g, '')
    .replace(/EDU_UPDATE/g, '')
    .replace(/\bEDU\b/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd()
}

// ─── Markdown Components ────────────────────────────────────────────────────────
const markdownComponents = {
  p: ({ children, ...props }: any) => (
    <p {...props} style={{ fontSize: 16, lineHeight: 1.75, marginBottom: '1em', color: TX, fontWeight: 450, letterSpacing: '0.01em' }}>{children}</p>
  ),
  strong: ({ children, ...props }: any) => (
    <strong {...props} style={{ fontWeight: 700, color: '#fff' }}>{children}</strong>
  ),
  em: ({ children, ...props }: any) => (
    <em {...props} style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>{children}</em>
  ),
  h1: ({ children, ...props }: any) => (
    <h1 {...props} style={{ fontSize: 24, fontWeight: 800, color: G, marginBottom: '0.6em', marginTop: '1em', letterSpacing: '-0.3px', lineHeight: 1.25, borderBottom: `1px solid ${BR}`, paddingBottom: '0.3em' }}>{children}</h1>
  ),
  h2: ({ children, ...props }: any) => (
    <h2 {...props} style={{ fontSize: 20, fontWeight: 700, color: '#f0e6cf', marginBottom: '0.5em', marginTop: '0.9em', lineHeight: 1.3 }}>{children}</h2>
  ),
  h3: ({ children, ...props }: any) => (
    <h3 {...props} style={{ fontSize: 17, fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: '0.4em', marginTop: '0.8em', lineHeight: 1.4 }}>{children}</h3>
  ),
  ul: ({ children, ...props }: any) => (
    <ul {...props} style={{ paddingLeft: '1.4em', marginBottom: '1em', listStyleType: 'none' }}>{children}</ul>
  ),
  ol: ({ children, ...props }: any) => (
    <ol {...props} style={{ paddingLeft: '1.4em', marginBottom: '1em', listStyleType: 'decimal', color: MU }}>{children}</ol>
  ),
  li: ({ children, ...props }: any) => (
    <li {...props} style={{ position: 'relative' as const, fontSize: 16, lineHeight: 1.7, color: TX, marginBottom: '0.5em' }}>
      {!props.ordered && <span style={{ position: 'absolute' as const, left: '-1.1em', color: G }}>•</span>}
      {children}
    </li>
  ),
  blockquote: ({ children, ...props }: any) => (
    <blockquote {...props} style={{ borderLeft: `4px solid ${G}`, padding: '16px 20px', margin: '1.5em 0', background: 'rgba(212,168,67,0.05)', borderRadius: '0 12px 12px 0', fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>{children}</blockquote>
  ),
  code: ({ children, className, ...props }: any) => {
    const isInline = !className
    return isInline ? (
      <code {...props} style={{ background: 'rgba(212,168,67,0.15)', color: '#f5e0a0', padding: '2px 6px', borderRadius: 4, fontSize: '0.925em', fontFamily: "'JetBrains Mono', monospace" }}>{children}</code>
    ) : (
      <code {...props} style={{ display: 'block', background: 'rgba(0,0,0,0.4)', color: '#e2dcca', padding: 20, borderRadius: 14, fontSize: 14, overflowX: 'auto' as const, fontFamily: "'JetBrains Mono', monospace", border: `1px solid ${BR}`, margin: '1.2em 0', lineHeight: 1.5 }}>{children}</code>
    )
  },
  a: ({ children, ...props }: any) => (
    <a {...props} style={{ color: IN, textDecoration: 'none', borderBottom: `1px solid ${IN}`, paddingBottom: 1 }} target="_blank" rel="noreferrer">{children}</a>
  ),
  hr: (props: any) => <hr {...props} style={{ border: 'none', borderTop: `1px solid ${BR}`, margin: '2em 0' }} />,
  table: ({ children, ...props }: any) => (
    <div style={{ overflowX: 'auto' as const, marginBottom: '1.2em', borderRadius: 12, border: `1px solid ${BR}` }}>
      <table {...props} style={{ borderCollapse: 'collapse' as const, width: '100%', fontSize: 14 }}>{children}</table>
    </div>
  ),
  th: ({ children, ...props }: any) => (
    <th {...props} style={{ padding: '12px 16px', textAlign: 'left' as const, fontWeight: 700, background: 'rgba(255,255,255,0.05)', borderBottom: `2px solid ${BR}`, color: G }}>{children}</th>
  ),
  td: ({ children, ...props }: any) => (
    <td {...props} style={{ padding: '10px 16px', borderBottom: `1px solid ${BR}`, color: TX }}>{children}</td>
  ),
}

// ─── Typing Indicator ───────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 5, padding: '16px 20px', alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <motion.div key={i}
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
          style={{ width: 7, height: 7, borderRadius: '50%', background: G }}
        />
      ))}
    </div>
  )
}

// ─── Media Parser ───────────────────────────────────────────────────────────────
// Parses [IMAGE: url | Caption: "..."] and [VIDEO: url | Caption: "..."]
// tags embedded by the AI engine in its markdown responses.
function parseMediaContent(content: string, isMobile: boolean): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  const regex = /\[(IMAGE|VIDEO):\s*([^\]|]+?)(?:\s*\|\s*(?:Caption:\s*"?)?([^"\]]*)"?)?\]/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(
        <ReactMarkdown key={`t-${lastIndex}`} remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {content.slice(lastIndex, match.index)}
        </ReactMarkdown>
      )
    }
    const type = match[1]
    const url = match[2].trim()
    const caption = match[3]?.trim() || ''

    if (type === 'IMAGE') {
      parts.push(
        <div key={`img-${match.index}`} style={{ margin: '16px 0', borderRadius: 14, overflow: 'hidden', border: `1px solid ${BR}`, background: CA }}>
          <img src={url} alt={caption || 'Lesson visual'}
            style={{ width: '100%', maxHeight: isMobile ? 240 : 360, objectFit: 'contain', display: 'block' }}
            loading="lazy"
          />
          {caption && <p style={{ padding: '10px 14px', fontSize: 12, color: MU, margin: 0, fontStyle: 'italic' }}>{caption}</p>}
        </div>
      )
    } else if (type === 'VIDEO') {
      parts.push(
        <div key={`vid-${match.index}`} style={{ margin: '16px 0', borderRadius: 14, overflow: 'hidden' }}>
          <SafeVideoPlayer videoUrl={url} title={caption || 'Lesson video'} className="w-full aspect-video" />
        </div>
      )
    }
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < content.length) {
    parts.push(
      <ReactMarkdown key="t-end" remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content.slice(lastIndex)}
      </ReactMarkdown>
    )
  }

  return parts.length > 0
    ? parts
    : [<ReactMarkdown key="full" remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>]
}

// ─── Message Bubble ─────────────────────────────────────────────────────────────
function MessageBubble({ content, role, isMobile }: {
  content: string; role: 'user' | 'assistant'; isMobile: boolean
}) {
  const cleaned = role === 'assistant' ? cleanEduContent(content) : content
  const hasMedia = role === 'assistant' && /\[(IMAGE|VIDEO):/.test(cleaned)

  return (
    <div style={{ display: 'flex', justifyContent: role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 20, gap: 10, alignItems: 'flex-start' }}>
      {role === 'assistant' && (
        <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${IN}, ${G})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, marginTop: 4 }}>
          🎓
        </div>
      )}
      <div style={{
        maxWidth: isMobile ? (role === 'user' ? '85%' : '82%') : '78%',
        padding: isMobile ? '13px 16px' : '15px 19px',
        borderRadius: role === 'user' ? '18px 18px 4px 18px' : '4px 18px 18px 18px',
        background: role === 'user' ? `linear-gradient(135deg, ${IN}, #0284c7)` : CA,
        border: role === 'user' ? 'none' : `1px solid ${BR}`,
        boxShadow: role === 'user' ? '0 4px 14px rgba(14,165,233,0.22)' : '0 2px 10px rgba(0,0,0,0.12)',
      }}>
        {role === 'assistant' ? (
          <div className="prose-university">
            {hasMedia
              ? parseMediaContent(cleaned, isMobile)
              : <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{cleaned}</ReactMarkdown>
            }
          </div>
        ) : (
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#fff', whiteSpace: 'pre-wrap', margin: 0 }}>{cleaned}</p>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── SessionInner ───────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function SessionInner({ classId, classInfo, moduleId, lessonId }: {
  classId: string
  classInfo: ClassInfo
  moduleId: number
  lessonId: number
}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const mod = classInfo.module_plan.find(m => m.module_id === moduleId)
  const lesson = mod?.lessons.find(l => l.lesson_id === lessonId)

  // ── Assessment kind derived from class essay_mode ──────────────────────────
  const essayMode = classInfo.essay_mode || 'none'
  // Index-based position — module_plan carries hierarchical IDs (42101 / 4210101)
  // but every comparison must be positional. Receipted 2026-10-02.
  const moduleIndex = classInfo.module_plan.findIndex(m => m.module_id === moduleId)
  const lessonIndex = mod ? mod.lessons.findIndex(l => l.lesson_id === lessonId) : -1
  const isLastLesson = lessonIndex >= 0 && lessonIndex === (mod?.lessons.length ?? 0) - 1
  const isLastModule = moduleIndex === classInfo.module_plan.length - 1
  const isCapstoneLesson = isLastLesson && isLastModule

  type AssessmentKind = 'short_answer' | 'essay' | 'none'
  let assessmentKind: AssessmentKind = 'none'
  if (essayMode === 'short_answer_only') {
    assessmentKind = 'short_answer'
  } else if (essayMode === 'essay_per_module' && isLastLesson) {
    assessmentKind = 'essay'
  } else if (essayMode === 'capstone_only' && isCapstoneLesson) {
    assessmentKind = 'essay'
  }

  // ── Hook ────────────────────────────────────────────────────────────────────
  // useUniversitySession is protocol-verified:
  //   - sends university_mode: true on all three paths
  //   - uses [quiz_request] / [quiz_submission] + quiz_answers protocol
  //   - fetchQuiz() is the correct intercept target for quiz chips/phrases
  const {
    messages, isLoading, isStreaming, error,
    sessionComplete, suggestedChips,
    quiz, quizOpen, quizLoading, quizResult,
    completedLessons, masteryScore,
    sendMessage, clearError, submitQuiz, dismissQuiz,
    updateEnrollmentProgress, fetchQuiz,
  } = useUniversitySession(
    classId,
    moduleId,
    lessonId,
    lesson?.title || '',
    lesson?.learning_objective || '',
    lesson?.core_concepts || [],
  )

  function isLessonDone(mId: number, lId: number, mIdx: number, lIdx: number): boolean {
    return !!(completedLessons[`${mId}-${lId}`]          // hierarchical keys (client-written)
      || completedLessons[`${mIdx + 1}-${lIdx + 1}`])    // sequential keys (scoreQuiz convention)
  }

  // ── Local UI state ──────────────────────────────────────────────────────────
  const [input, setInput] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  const [showSidebar, setShowSidebar] = useState(false)
  const [showMilestone, setShowMilestone] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // ── Responsive detection ────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth <= 820
      setIsMobile(mobile)
      if (!mobile) setShowSidebar(true)
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // ── Auto-start on empty conversation ───────────────────────────────────────
  const hasAutoStartedRef = useRef(false)
  useEffect(() => {
    if (
      !hasAutoStartedRef.current &&
      messages.length === 0 &&
      !isLoading &&
      !isStreaming
    ) {
      hasAutoStartedRef.current = true
      sendMessage('start the lesson')
    }
  }, [messages.length, isLoading, isStreaming, sendMessage])

  // ── Initial scroll for returning learners ──────────────────────────────────
  const didInitialScrollRef = useRef(false)
  useEffect(() => {
    if (didInitialScrollRef.current) return
    if (messages.length === 0 || isLoading || isStreaming) return
    didInitialScrollRef.current = true
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ block: 'end' })
    })
  }, [messages.length, isLoading, isStreaming])

  // ── Auto-scroll ─────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  // ── Module milestone: fires 1.2s after last-lesson quiz passes ─────────────
  useEffect(() => {
    if (quizResult?.passed && isLastLesson && !showMilestone) {
      const timer = setTimeout(() => setShowMilestone(true), 1200)
      return () => clearTimeout(timer)
    }
  }, [quizResult?.passed, isLastLesson, showMilestone])

  // ── Send / quiz intercept ───────────────────────────────────────────────────
  // "take quiz" phrases route to fetchQuiz() ([quiz_request] protocol path).
  // They must never reach sendMessage() — the stall machine counts no-progress turns.
  function handleSend() {
    const text = input.trim()
    if (!text || isLoading || isStreaming) return
    setInput('')
    if (/\b(start|take|launch|begin|do|ready for|give me)\s*(the\s+)?quiz\b/i.test(text)) {
      fetchQuiz()
      return
    }
    sendMessage(text)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  // ── Navigation ──────────────────────────────────────────────────────────────
  function goNextLesson() {
    if (!mod || lessonIndex < 0) return
    const nextInModule = mod.lessons[lessonIndex + 1]
    if (nextInModule) {
      navigate(`/university/learn/${classId}?module=${moduleId}&lesson=${nextInModule.lesson_id}`)
    } else {
      const nextMod = classInfo.module_plan[moduleIndex + 1]
      nextMod
        ? navigate(`/university/learn/${classId}?module=${nextMod.module_id}&lesson=${nextMod.lessons[0].lesson_id}`)
        : navigate(`/university/degree/${classId}`)
    }
  }

  // ── Derived progress values ─────────────────────────────────────────────────
  const totalLessonCount = classInfo.module_plan.reduce((a, m) => a + (m.lessons?.length || 0), 0)
  const completedCount = Object.values(completedLessons).filter(Boolean).length
  const progressPct = totalLessonCount > 0 ? Math.round((completedCount / totalLessonCount) * 100) : 0

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100dvh', background: M, overflow: 'hidden', fontFamily: "'Inter', sans-serif" }}>
      <SEO title={lesson?.title || classInfo.name} noindex />
      <style>{`
        .prose-university p:last-child { margin-bottom: 0; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* ── Module Milestone Modal ── */}
      <ModuleMilestone
        isOpen={showMilestone}
        onClose={() => setShowMilestone(false)}
        moduleTitle={mod?.title || `Module ${moduleId}`}
        degreeName={classInfo.name}
      />

      {/* ── Mobile sidebar backdrop ── */}
      <AnimatePresence>
        {isMobile && showSidebar && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowSidebar(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, backdropFilter: 'blur(4px)' }}
          />
        )}
      </AnimatePresence>

      {/* ═══ SIDEBAR ═══ */}
      <motion.div
        animate={{ x: isMobile ? (showSidebar ? 0 : -280) : 0, width: isMobile ? 280 : 260 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        style={{
          position: isMobile ? 'fixed' : 'relative',
          height: '100%', flexShrink: 0,
          borderRight: `1px solid ${BR}`,
          background: isMobile ? '#121225' : 'rgba(255,255,255,0.02)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 101,
        }}
      >
        {/* Sidebar header */}
        <div style={{ padding: '24px 20px 16px', borderBottom: `1px solid ${BR}` }}>
          <button
            onClick={() => navigate(`/university/degree/${classId}`)}
            style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontSize: 11, padding: 0, marginBottom: 12, fontWeight: 700, letterSpacing: '0.5px' }}
          >
            ← {t('university.session.backToDegree', 'DEGREE OVERVIEW')}
          </button>
          <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '1.5px', marginBottom: 6, textTransform: 'uppercase' }}>
            {t('university.session.lesson', 'Lesson {{n}}', { n: lessonId })}
          </p>
          <p style={{ fontSize: 15, fontWeight: 700, color: TX, lineHeight: 1.3 }}>{lesson?.title}</p>
        </div>

        {/* Sidebar scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }} className="scrollbar-hide">

          {/* Progress + mastery widget */}
          <div style={{ background: 'rgba(212,168,67,0.06)', borderRadius: 14, padding: 12, marginBottom: 20, border: '1px solid rgba(212,168,67,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: G, letterSpacing: 0.5 }}>PROGRESS</span>
              <span style={{ fontSize: 10, fontWeight: 800, color: G }}>{progressPct}%</span>
            </div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: 8 }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${progressPct}%` }} style={{ height: '100%', background: G }} />
            </div>

            {/* Mastery score — color-coded: green ≥80%, gold ≥60%, red <60% */}
            {masteryScore > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: MU, letterSpacing: 0.8 }}>MASTERY</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: masteryScore >= 80 ? '#4ade80' : masteryScore >= 60 ? G : '#f87171' }}>
                  {Math.round(masteryScore)}%
                </span>
              </div>
            )}

            {/* Core concept chips */}
            {lesson?.core_concepts && lesson.core_concepts.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <p style={{ fontSize: 9, fontWeight: 800, color: MU, marginBottom: 6, letterSpacing: 1 }}>{t('university.session.core_concepts')}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {lesson.core_concepts.slice(0, 5).map(c => (
                    <span key={c} style={{ fontSize: 9, background: 'rgba(255,255,255,0.04)', color: TX, padding: '3px 6px', borderRadius: 4, border: `1px solid ${BR}` }}>{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Module + lesson list */}
          {classInfo.module_plan.map((m, mIdx) => (
            <div key={m.module_id} style={{ marginBottom: 6 }}>
              <div style={{ padding: '8px 10px', fontSize: 11, fontWeight: 800, color: m.module_id === moduleId ? G : MU, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                {m.title}
              </div>
              {m.module_id === moduleId && m.lessons.map((l, lIdx) => {
                const done = isLessonDone(m.module_id, l.lesson_id, mIdx, lIdx)
                const isActive = l.lesson_id === lessonId
                return (
                  <button key={l.lesson_id}
                    onClick={() => {
                      navigate(`/university/learn/${classId}?module=${moduleId}&lesson=${l.lesson_id}`)
                      if (isMobile) setShowSidebar(false)
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: isActive ? 'rgba(212,168,67,0.12)' : 'none', border: 'none', cursor: 'pointer', padding: '10px 12px', borderRadius: 10, textAlign: 'left', marginBottom: 2 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, background: done ? '#4ade80' : isActive ? G : 'rgba(255,255,255,0.08)', color: done || isActive ? '#06060c' : MU }}>
                      {done ? '✓' : l.lesson_id}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: isActive ? 600 : 500, color: isActive ? TX : MU, lineHeight: 1.4 }}>{l.title}</span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </motion.div>

      {/* ═══ CHAT AREA ═══ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>

        {/* Top bar — no UniversityHeader mount; this is the session's own top bar */}
        <div style={{ padding: isMobile ? '0 12px' : '0 24px', height: 64, display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${BR}`, flexShrink: 0, background: 'rgba(13,13,26,0.8)', backdropFilter: 'blur(10px)', zIndex: 10 }}>
          {isMobile && (
            <button onClick={() => setShowSidebar(!showSidebar)} style={{ background: 'none', border: 'none', color: TX, cursor: 'pointer', padding: 0, marginRight: 4 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg, ${IN}, ${G})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🎓</div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <p style={{ fontSize: 14, fontWeight: 800, color: TX, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{classInfo.name}</p>
            <p style={{ fontSize: 11, color: MU, margin: 0, fontWeight: 600 }}>{mod?.title}</p>
          </div>
          <LanguagePicker />
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 14px 8px' : '24px 24px 16px' }} className="scrollbar-hide">

          {messages.length === 0 && !isLoading && (
            <div style={{ textAlign: 'center', paddingTop: isMobile ? 40 : 60 }}>
              <div style={{ fontSize: isMobile ? 32 : 40, marginBottom: 16 }}>🎓</div>
              <p style={{ color: MU, fontSize: 14, maxWidth: 260, margin: '0 auto', lineHeight: 1.5 }}>
                {t('university.session.intro', 'Your professor is ready. Ask a question or say hello to begin.')}
              </p>
            </div>
          )}

          {messages.map(msg => (
            <MessageBubble key={msg.id} content={msg.content} role={msg.role} isMobile={isMobile} />
          ))}

          {/* Typing dots — only while waiting for first assistant token */}
          {(isLoading || isStreaming) && (messages.length === 0 || messages[messages.length - 1]?.role === 'user') && (
            <div style={{ display: 'flex', marginBottom: 20, gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${IN}, ${G})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🎓</div>
              <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: '4px 18px 18px 18px' }}><TypingDots /></div>
            </div>
          )}

          {/* Error banner */}
          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#f87171', fontWeight: 500 }}>{error}</span>
                <button onClick={clearError} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Assessments ──────────────────────────────────────────────────────
              EssayDropbox-first: authored rubric'd assignments (139 rows) take
              priority. ShortAnswerBox is the fallback for short_answer_only mode. */}
          {sessionComplete && assessmentKind === 'essay' && (
            <EssayDropbox
              classId={classId}
              moduleId={moduleId}
              className={classInfo.name}
            />
          )}
          {sessionComplete && assessmentKind === 'short_answer' && (
            <ShortAnswerBox
              classId={classId}
              moduleId={moduleId}
              lessonId={lessonId}
              onDismiss={goNextLesson}
              onComplete={() => { /* refresh mastery via re-fetch if needed */ }}
            />
          )}

          {/* ── Lesson complete banner ── */}
          <AnimatePresence>
            {sessionComplete && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                style={{ background: 'rgba(212,168,67,0.1)', border: '1.5px solid rgba(212,168,67,0.4)', borderRadius: 20, padding: isMobile ? 20 : 24, marginBottom: 16, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <p style={{ fontWeight: 800, fontSize: 17, color: G, marginBottom: 4, letterSpacing: '-0.3px' }}>
                    {quizResult
                      ? (quizResult.passed ? '🎉 Lesson Passed!' : '📈 Keep Going!')
                      : `🎉 ${t('university.session.lessonDone', 'Lesson Complete!')}`}
                  </p>
                  <p style={{ fontSize: 13, color: MU, fontWeight: 500 }}>
                    {quizResult && !quizResult.passed
                      ? `You scored ${quizResult.score}%. Review the feedback above and retake when ready.`
                      : isCapstoneLesson
                        ? t('university.session.lessonDoneCapstone', "You've completed this degree. Head back to the overview.")
                        : isLastLesson
                          ? t('university.session.lessonDoneModule', 'Module complete! Continue to the next module when ready.')
                          : t('university.session.lessonDoneHint', 'Excellent progress. Move to the next lesson or keep discussing.')}
                  </p>
                  {masteryScore > 0 && quizResult?.passed && (
                    <p style={{ fontSize: 12, color: G, fontWeight: 700, marginTop: 6 }}>
                      Mastery: {Math.round(masteryScore)}%
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                  {/* Retake button — routes back through fetchQuiz(), not sendMessage() */}
                  {quizResult && !quizResult.passed && (
                    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                      onClick={() => fetchQuiz()}
                      style={{ background: 'rgba(255,255,255,0.05)', color: TX, border: `1px solid ${BR}`, borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      Retake Quiz
                    </motion.button>
                  )}
                  {assessmentKind === 'none' && (
                    <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={goNextLesson}
                      style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 800, cursor: 'pointer', flex: 1 }}>
                      {isCapstoneLesson
                        ? `${t('university.session.backToDegreeBtn', 'Degree Overview')} →`
                        : isLastLesson
                          ? `${t('university.session.nextModule', 'Next Module')} →`
                          : `${t('university.session.next', 'Next Lesson')} →`}
                    </motion.button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Quiz loading indicator */}
          <AnimatePresence>
            {quizLoading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.25)', borderRadius: 16, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                  style={{ width: 20, height: 20, borderRadius: '50%', border: '2.5px solid rgba(14,165,233,0.2)', borderTopColor: IN, flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: IN, fontWeight: 600 }}>
                  {t('university.session.quizLoading', 'Generating your quiz…')}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Suggested chips — quiz-keyword chips route to fetchQuiz(), others to sendMessage() */}
          {suggestedChips.length > 0 && !quizOpen && !quizLoading && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {suggestedChips.map(chip => (
                <motion.button key={chip}
                  onClick={() => {
                    if (/quiz/i.test(chip)) { fetchQuiz(); return }
                    sendMessage(chip)
                  }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.25)', borderRadius: 100, padding: '9px 18px', fontSize: 13, color: '#7dd3fc', cursor: 'pointer', fontWeight: 600 }}>
                  {chip}
                </motion.button>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Quiz overlay ───────────────────────────────────────────────────── */}
        {quiz && (
          <QuizInterface
            quiz={quiz}
            isOpen={quizOpen}
            onSubmit={submitQuiz}
            onDismiss={dismissQuiz}
            categoryName={classInfo.name}
          />
        )}

        {/* Input */}
        <div style={{ padding: isMobile ? '12px 14px 20px' : '16px 24px 24px', borderTop: `1px solid ${BR}`, flexShrink: 0, background: 'rgba(13,13,26,0.6)', backdropFilter: 'blur(20px)' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isMobile
                  ? t('university.session.placeholderMobile', 'Type your message...')
                  : t('university.session.placeholder', 'Ask your professor anything… (Enter to send)')
              }
              rows={1}
              style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${BR}`, borderRadius: 16, padding: isMobile ? '12px 14px' : '14px 18px', color: TX, fontSize: 15, outline: 'none', resize: 'none', fontFamily: "'Inter', sans-serif", lineHeight: 1.5, maxHeight: 120, transition: 'border-color 0.2s' }}
              onFocus={e => (e.target.style.borderColor = 'rgba(212,168,67,0.4)')}
              onBlur={e => (e.target.style.borderColor = BR)}
            />
            <motion.button
              whileHover={!isMobile ? { scale: 1.05 } : {}}
              whileTap={{ scale: 0.92 }}
              onClick={handleSend}
              disabled={!input.trim() || isLoading || isStreaming}
              style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0, background: input.trim() && !isLoading && !isStreaming ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.05)', border: 'none', cursor: input.trim() ? 'pointer' : 'not-allowed', color: input.trim() ? '#06060c' : MU, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
              </svg>
            </motion.button>
          </div>
          {!isMobile && (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', textAlign: 'center', marginTop: 10, fontWeight: 500 }}>
              {t('university.session.footerHint', 'Shift+Enter for new line · Professor knows the context of this lesson')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── SessionPage (outer) — entitlement + enrollment gate ───────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

export default function SessionPage() {
  const { t } = useTranslation()
  const { classId } = useParams<{ classId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  // userProfile carries preferences.billing.plan and university_access boolean
  const { user, userProfile, isLoading: authLoading } = useAuth()

  const moduleId = parseInt(searchParams.get('module') || '1')
  const lessonId = parseInt(searchParams.get('lesson') || '1')

  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [entry, setEntry] = useState<{ module: number; lesson: number } | null>(null)

  // ── Auth guard ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && !user) {
      const next = encodeURIComponent(location.pathname + location.search)
      navigate(`/signin?next=${next}`, { replace: true })
    }
  }, [user, authLoading, navigate, location])

  // ── Load class + entitlement + enrollment check ────────────────────────────
  useEffect(() => {
    if (!user || !classId) return
    let cancelled = false

    async function load() {
      try {
        const { data: cls } = await supabase
          .from('classes')
          .select('id, name, module_plan, essay_mode, curriculum_track')
          .eq('id', classId!)
          .single()

        if (cancelled || !cls) { if (!cancelled) setLoading(false); return }

        // Faith courses are taught on the church surface — church tone, sage_source 'church'.
        if (cls?.curriculum_track === 'faith') {
          navigate(`/church/session/${classId}`, { replace: true })
          return
        }

        // Check entitlements: university_* plan key, university_access boolean, admin override, or elite/standard superset
        const entitlements = getEntitlements(userProfile as Record<string, unknown> | null)

        if (!entitlements.university && !entitlements.isPaid) {
          // Fallback for NGO/corporate learners who hold an enrollment row
          // but whose access doesn't flow through a subscription plan key.
          const { data: enrollment } = await supabase
            .from('class_enrollments')
            .select('id')
            .eq('student_id', user!.id)
            .eq('class_id', classId!)
            .maybeSingle()

          if (!enrollment) {
            // No entitlement AND no enrollment row → gate out, redirect to enrol.
            // Does NOT render an inline paywall — consistent with the gate pattern
            // used across Education and Church session pages.
            if (!cancelled) navigate('/university/enrol', { replace: true })
            return
          }
        }

        if (!cancelled) { setClassInfo(cls as unknown as ClassInfo); setLoading(false) }
      } catch {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [user, userProfile, classId, navigate])

  // ── Entry position ────────────────────────────────────────────────────────
  // URL params win when they match the plan. Otherwise resolve from server
  // progress: progress_state stores SEQUENTIAL 1-based positions (receipted:
  // current_unit values are 1, 2 platform-wide), the plan carries hierarchical
  // IDs — bridge by index, never by value.
  useEffect(() => {
    if (!user || !classId || !classInfo) return
    const p = classInfo.module_plan
    if (!p || p.length === 0) return

    const matchedMod = p.find(m => m.module_id === moduleId)
    if (matchedMod) {
      const has = (matchedMod.lessons ?? []).some(l => l.lesson_id === lessonId)
      setEntry(has ? null : { module: moduleId, lesson: (matchedMod.lessons ?? [])[0]?.lesson_id ?? lessonId })
      return
    }

    let cancelled = false
    ;(async () => {
      const { data: enrol } = await supabase
        .from('class_enrollments')
        .select('progress_state')
        .eq('student_id', user!.id)
        .eq('class_id', classId!)
        .maybeSingle()
      if (cancelled) return
      const ps = enrol?.progress_state as { current_unit?: number; current_lesson?: number } | null
      const mi = Math.min(Math.max((ps?.current_unit ?? 1) - 1, 0), p.length - 1)
      const m = p[mi]
      const lessons = m?.lessons ?? []
      const li = Math.min(Math.max((ps?.current_lesson ?? 1) - 1, 0), Math.max(lessons.length - 1, 0))
      setEntry({ module: m?.module_id ?? moduleId, lesson: lessons[li]?.lesson_id ?? lessons[0]?.lesson_id ?? lessonId })
    })()
    return () => { cancelled = true }
  }, [classInfo, user, classId, moduleId, lessonId])

  // ── Loading spinner ────────────────────────────────────────────────────────
  const plan = classInfo?.module_plan ?? []
  const paramsValid = !!plan.find(m => m.module_id === moduleId &&
                                       (m.lessons ?? []).some(l => l.lesson_id === lessonId))
  const awaitingEntry = !!classInfo && !paramsValid && entry === null

  if (authLoading || (user && loading) || awaitingEntry) {
    return (
      <div style={{ background: M, height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
          style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid rgba(212,168,67,0.15)', borderTopColor: G }} />
      </div>
    )
  }

  if (!classInfo) {
    return (
      <div style={{ background: M, height: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: TX }}>
        <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{t('university.session.class_not_found')}</p>
        <button onClick={() => navigate('/university')}
          style={{ background: G, color: '#06060c', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, cursor: 'pointer' }}>
          Back to University
        </button>
      </div>
    )
  }

  return (
    <SessionInner
      classId={classId!}
      classInfo={classInfo}
      moduleId={entry?.module ?? moduleId}
      lessonId={entry?.lesson ?? lessonId}
    />
  )
}
